import { Smartphone } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { AppleLogo, GooglePlayLogo } from "./StoreLogos";
import { env } from "@/lib/env";

interface DownloadAppCTAProps {
  /**
   * Customise the headline for the surface the CTA lives on.
   * Defaults to a generic "Get the app" pitch.
   */
  headline?: string;
  /** Subhead under the headline. */
  subhead?: string;
  /** Compact = inline pair of small buttons. Default = a card. */
  variant?: "card" | "compact";
  className?: string;
}

/**
 * Reusable "download the Whistll app" call-to-action. Lives on the
 * public-facing booking surfaces (court detail, match detail) so a
 * curious player who lands on a deep link knows the actual
 * roster/RSVP/host flow lives in the iOS / Android app, not the
 * marketing site.
 *
 * If the env var for either store URL isn't set, we still render the
 * button but disabled - better than the CTA mysteriously vanishing
 * pre-launch.
 */
export function DownloadAppCTA({
  headline = "Booking happens in the Whistll app",
  subhead = "Browse runs, secure your seat with a Stripe hold, and chat with your team - all from your phone.",
  variant = "card",
  className,
}: DownloadAppCTAProps) {
  if (variant === "compact") {
    return (
      <div className={cn("flex flex-wrap items-center gap-2", className)}>
        {env.iosAppUrl ? (
          <Button asChild size="sm" variant="outline">
            <a href={env.iosAppUrl}>
              <AppleLogo size={14} />
              App Store
            </a>
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled>
            <AppleLogo size={14} />
            iOS soon
          </Button>
        )}
        {env.androidAppUrl ? (
          <Button asChild size="sm" variant="outline">
            <a href={env.androidAppUrl}>
              <GooglePlayLogo size={14} />
              Play
            </a>
          </Button>
        ) : (
          <Button size="sm" variant="outline" disabled>
            <GooglePlayLogo size={14} />
            Android soon
          </Button>
        )}
      </div>
    );
  }

  return (
    <section
      className={cn(
        "relative overflow-hidden rounded-2xl border border-volt/30 bg-volt-subtle p-5 sm:p-6",
        className,
      )}
    >
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-start gap-3">
          <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-volt/15 text-volt">
            <Smartphone className="h-4 w-4" />
          </div>
          <div>
            <p className="font-semibold">{headline}</p>
            <p className="mt-1 max-w-xl text-sm text-muted-foreground">
              {subhead}
            </p>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {env.iosAppUrl ? (
            <Button asChild size="sm">
              <a href={env.iosAppUrl}>
                <AppleLogo size={16} />
                App Store
              </a>
            </Button>
          ) : (
            <Button size="sm" disabled>
              <AppleLogo size={16} />
              App Store soon
            </Button>
          )}
          {env.androidAppUrl ? (
            <Button asChild size="sm" variant="outline">
              <a href={env.androidAppUrl}>
                <GooglePlayLogo size={16} />
                Google Play
              </a>
            </Button>
          ) : (
            <Button size="sm" variant="outline" disabled>
              <GooglePlayLogo size={16} />
              Google Play soon
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function cn(...parts: Array<string | undefined | false>): string {
  return parts.filter(Boolean).join(" ");
}
